


import React, { useRef, useState, useEffect } from "react";
import {
  Input,
  Upload,
  Button,
  Row,
  Col,
  Checkbox,
  Card,
  Typography,
  Space,
  message,
  Switch,
  Tooltip,
  Alert
} from "antd";
import {
  UploadOutlined,
  FilePdfOutlined,
  GlobalOutlined,
  LinkOutlined,
  DownloadOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  HighlightOutlined
} from "@ant-design/icons";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { QRCodeCanvas } from "qrcode.react";
import { Editor } from "@tinymce/tinymce-react";
import courseService from "../../services/course.service";
import axios from "axios";
import API_BASE from "../../config/apiConfig";

const DEFAULT_LOGO = "/img/nomidya2.png";
const DEFAULT_BACKGROUND = "/img/2.jpg";

const EXAMPLE_CERT_NUMBER = "CERT-EXEMPLE-0000";

const CERT_NUMBER_LINE =
  '<p style="text-align:left; font-size:11px; color:#888888;">Certificat n° {{certNumber}} - Délivré le {{date}}</p>';

const buildSignatureImgTag = (base64, w, h) =>
  `<img src="${base64}" data-signature="true" alt="Signature" ` +
  `style="width:${w}px;height:${h}px;object-fit:contain;background:transparent;` +
  `mix-blend-mode:multiply;display:inline-block;" />`;

const signatureImgToPlaceholder = (html) => {
  if (!html) return html;
  return html.replace(
    /<img[^>]*data-signature=["']true["'][^>]*>/gi,
    "{{signature}}"
  );
};

const placeholderToSignatureImg = (html, base64, w, h) => {
  if (!html) return html;
  if (!base64) {
    return html.replace(
      /\{\{signature\}\}/gi,
      '<span style="color:#bbb;font-style:italic;">[signature]</span>'
    );
  }
  return html.replace(/\{\{signature\}\}/gi, buildSignatureImgTag(base64, w, h));
};

// 🔧 NETTOYAGE MAXIMAL : retire TOUS les traits / lignes / bordures parasites
//    SANS toucher au texte ("Direction des programmes SAP", "Fait le", numéro,
//    nom, cours) ni à la signature (data-signature) ni au QR.
// const removeOldSignatureLines = (html) => {
//   let h = html || "";

//   // --- 1. Underscores / tirets longs en paragraphe ou inline ---
//   h = h.replace(/<p[^>]*>\s*[_\u2014\u2013\-—–]{2,}\s*<\/p>/gi, "");
//   h = h.replace(/[_]{3,}/g, "");
//   h = h.replace(/[\u2014\u2013]{3,}/g, "");

//   // --- 2. Balises <hr> sous toutes leurs formes ---
//   h = h.replace(/<hr[^>]*\/?>/gi, "");

//   // --- 3. Retirer toute déclaration de bordure dans les style="" ---
//   //    (border, border-bottom/top/left/right, border-width, border-style)
//   //    mais on garde le reste du style intact.
//   h = h.replace(/border(-top|-bottom|-left|-right)?\s*:\s*[^;"']+;?/gi, "");
//   h = h.replace(/border(-top|-bottom|-left|-right)?-(width|style|color)\s*:\s*[^;"']+;?/gi, "");

//   // --- 4. text-decoration:underline / overline sur éléments QUASI VIDES ---
//   //    (séparateurs vides). Le souligné sur du vrai texte est conservé.
//   h = h.replace(
//     /<(span|p|u|div)[^>]*(text-decoration\s*:\s*(underline|overline))[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/\1>/gi,
//     ""
//   );

//   // --- 5. <table>/<tr>/<td> "invisibles" qui servaient à dessiner des lignes ---
//   //    On retire les tables dont les cellules sont vides (lignes de signature).
//   h = h.replace(
//     /<table[^>]*>(\s*<tbody[^>]*>)?\s*(<tr[^>]*>\s*(<t[dh][^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/t[dh]>\s*)+<\/tr>\s*)+(<\/tbody>)?\s*<\/table>/gi,
//     ""
//   );

//   // --- 6. Paragraphes / spans / divs totalement vides (résidus) ---
//   h = h.replace(/<p[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, "");
//   h = h.replace(/<span[^>]*>(\s|&nbsp;)*<\/span>/gi, "");
//   h = h.replace(/<div[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/div>/gi, "");

//   // --- 7. style="" devenu vide ou ne contenant que ; et espaces ---
//   h = h.replace(/\s*style\s*=\s*["']\s*;*\s*["']/gi, "");

//   return h;
// };

// 🔧 NETTOYAGE CIBLÉ : retire UNIQUEMENT les vrais traits / lignes parasites
//    (hr, underscores longs, bordures vides) SANS toucher au contenu réel
//    (texte, liens, paragraphes avec contenu utile comme le pied de page).
const removeOldSignatureLines = (html) => {
  if (!html) return html;
  let h = html;

  // --- 1. Underscores / tirets longs SEULS dans un paragraphe ---
  h = h.replace(/<p[^>]*>\s*[_\u2014\u2013\-—–]{2,}\s*<\/p>/gi, "");

  // --- 2. Underscores longs hors paragraphe (3+ d'affilée) ---
  h = h.replace(/[_]{3,}/g, "");
  h = h.replace(/[\u2014\u2013]{3,}/g, "");

  // --- 3. Balises <hr> sous toutes leurs formes ---
  h = h.replace(/<hr[^>]*\/?>/gi, "");

  // --- 4. text-decoration:underline/overline UNIQUEMENT sur éléments VIDES ---
  //    (séparateurs vides). Le souligné sur du vrai texte est conservé.
  h = h.replace(
    /<(span|p|u|div)[^>]*(text-decoration\s*:\s*(underline|overline))[^>]*>(\s|&nbsp;|<br\s*\/?>)*<\/\1>/gi,
    ""
  );

  // ⚠️ ON NE TOUCHE PLUS À :
  //   - border / border-bottom / border-top dans les style=""
  //     (ça cassait le pied de page)
  //   - <table>/<tr>/<td>
  //     (TinyMCE utilise des tables pour aligner du texte)
  //   - <p>/<span>/<div> vides
  //     (un <p> avec uniquement des liens <a> peut être vu comme "vide" par la regex)
  //
  //   Si tu as VRAIMENT des traits parasites qui reviennent, utilise le bouton
  //   "Nettoyer les traits" dans l'UI — il ne s'applique qu'à la demande.

  return h;
};

const CertificateGenerator = ({ studentName, courseTitle, courseId }) => {
  const certRef = useRef(null);
  const editorRef = useRef(null);

  const [qrLink, setQrLink] = useState("");
  const [logoPreview, setLogoPreview] = useState(DEFAULT_LOGO);
  const [backgroundPreview, setBackgroundPreview] = useState(DEFAULT_BACKGROUND);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [signatureWidth, setSignatureWidth] = useState(185);
  const [signatureHeight, setSignatureHeight] = useState(96);

  const [backgroundColor, setBackgroundColor] = useState("#ffffff");
  const [textColor, setTextColor] = useState("#000000");
  const [useImageBackground, setUseImageBackground] = useState(true);
  const [paddingTop, setPaddingTop] = useState(40);
  const [paddingBottom, setPaddingBottom] = useState(40);
  const [logoTopSpacing, setLogoTopSpacing] = useState(30);
  const [logoMarginBottom, setLogoMarginBottom] = useState(20);
  const [logoWidth, setLogoWidth] = useState(100);
  const [logoHeight, setLogoHeight] = useState(100);
  const [backgroundWidth, setBackgroundWidth] = useState(650);
  const [backgroundHeight, setBackgroundHeight] = useState(610);
  const [uploadedImageLink, setUploadedImageLink] = useState(null);
  const [threshold, setThreshold] = useState(70);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [loadingTest, setLoadingTest] = useState(false);
  const [globalTemplate, setGlobalTemplate] = useState(false);
  const [lastSavedInfo, setLastSavedInfo] = useState(null);
  const [certificateHtmlContent, setCertificateHtmlContent] = useState("");

  useEffect(() => {
    const safeCourse = (courseTitle || "").replace(/\s+/g, "-").toLowerCase();
    setQrLink(`https://nomidya.com/certificate/verify/preview-${safeCourse}`);
  }, [courseTitle]);
// ✅ HTML par défaut UNIQUEMENT si l'éditeur est vide
  //    (n'écrase JAMAIS le template sauvegardé en base)
  useEffect(() => {
    if (certificateHtmlContent && certificateHtmlContent.trim()) {
      return; // déjà du contenu, on ne touche pas
    }
    const today = new Date().toLocaleDateString();
    setCertificateHtmlContent(`
      <h1 style="text-align:center;">Certificat de réussite</h1>
      <p>Ce certificat est décerné à :</p>
      <h2 style="font-weight:bold;">${studentName}</h2>
      <p>pour avoir complété le cours :</p>
      <h3 style="font-style:italic;">${courseTitle}</h3>
      <br/>
      <p style="font-weight:bold;">Direction des programmes SAP</p>
      <p style="text-align:center;">{{signature}}</p>
      <p style="text-align:center;">Fait le : ${today}</p>
      ${CERT_NUMBER_LINE}
    `);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentName, courseTitle]);
  // useEffect(() => {
  //   const today = new Date().toLocaleDateString();
  //   setCertificateHtmlContent(`
  //     <h1 style="text-align:center;">Certificat de réussite</h1>
  //     <p>Ce certificat est décerné à :</p>
  //     <h2 style="font-weight:bold;">${studentName}</h2>
  //     <p>pour avoir complété le cours :</p>
  //     <h3 style="font-style:italic;">${courseTitle}</h3>
  //     <br/>
  //     <p style="font-weight:bold;">Direction des programmes SAP</p>
  //     <p style="text-align:center;">{{signature}}</p>
  //     <p style="text-align:center;">Fait le : ${today}</p>
  //     ${CERT_NUMBER_LINE}
  //   `);
  // }, [studentName, courseTitle]);

  useEffect(() => {
    if (!courseId) return;
    const loadLastTemplate = async () => {
      try {
        const template = await courseService.getLastTemplate(courseId);
        console.log("📦 Template reçu du backend:", template);

        if (template) {
          setLastSavedInfo({
            date: template.dateCreation,
            global: template.globalTemplate,
          });
          if (template.threshold) setThreshold(template.threshold);
          if (template.globalTemplate !== undefined) setGlobalTemplate(template.globalTemplate);

          let parsedSettings = {};
          if (template.templateSettings) {
            try {
              parsedSettings = JSON.parse(template.templateSettings);
              console.log("⚙️ Settings parsés:", {
                ...parsedSettings,
                backgroundPreview: parsedSettings.backgroundPreview
                  ? parsedSettings.backgroundPreview.substring(0, 50) + "..."
                  : "❌ ABSENT",
                logoPreview: parsedSettings.logoPreview
                  ? parsedSettings.logoPreview.substring(0, 50) + "..."
                  : "❌ ABSENT",
                signaturePreview: parsedSettings.signaturePreview
                  ? parsedSettings.signaturePreview.substring(0, 50) + "..."
                  : "❌ ABSENT",
              });

              if (parsedSettings.backgroundColor) setBackgroundColor(parsedSettings.backgroundColor);
              if (parsedSettings.textColor) setTextColor(parsedSettings.textColor);
              if (parsedSettings.logoWidth) setLogoWidth(parsedSettings.logoWidth);
              if (parsedSettings.logoHeight) setLogoHeight(parsedSettings.logoHeight);
              if (parsedSettings.paddingTop) setPaddingTop(parsedSettings.paddingTop);
              if (parsedSettings.paddingBottom) setPaddingBottom(parsedSettings.paddingBottom);
              if (parsedSettings.useImageBackground !== undefined) setUseImageBackground(parsedSettings.useImageBackground);
              if (parsedSettings.backgroundWidth) setBackgroundWidth(parsedSettings.backgroundWidth);
              if (parsedSettings.backgroundHeight) setBackgroundHeight(parsedSettings.backgroundHeight);
              if (parsedSettings.signatureWidth) setSignatureWidth(parsedSettings.signatureWidth);
              if (parsedSettings.signatureHeight) setSignatureHeight(parsedSettings.signatureHeight);
            } catch (e) {
              console.error("❌ Erreur parsing settings:", e);
            }
          } else {
            console.warn("⚠️ Aucun templateSettings dans le template");
          }

          if (parsedSettings.backgroundPreview) {
            console.log("✅ Restauration backgroundPreview (base64)");
            setBackgroundPreview(parsedSettings.backgroundPreview);
          } else {
            console.warn("⚠️ Pas de backgroundPreview — fond par défaut");
          }

          if (parsedSettings.logoPreview) {
            console.log("✅ Restauration logoPreview (base64)");
            setLogoPreview(parsedSettings.logoPreview);
          } else {
            console.warn("⚠️ Pas de logoPreview — logo par défaut");
          }

          let sigBase64 = null;
          if (parsedSettings.signaturePreview) {
            console.log("✅ Restauration signaturePreview (base64)");
            sigBase64 = parsedSettings.signaturePreview;
            setSignaturePreview(parsedSettings.signaturePreview);
          } else {
            console.warn("⚠️ Pas de signaturePreview dans settings");
          }

          if (template.templateHtml) {
            let html = template.templateHtml
              .replace(/\{\{courseName\}\}/gi, courseTitle || "Cours")
              .replace(/\{\{username\}\}/gi, studentName || "Nom")
              .replace(/\{\{date\}\}/gi, new Date().toLocaleDateString());

            // 🔧 NETTOYAGE MAXIMAL des traits parasites
            html = removeOldSignatureLines(html);

            if (!/\{\{certNumber\}\}/i.test(html)) {
              html += "\n" + CERT_NUMBER_LINE;
            }

            html = placeholderToSignatureImg(
              html,
              sigBase64,
              parsedSettings.signatureWidth || signatureWidth,
              parsedSettings.signatureHeight || signatureHeight
            );

            setCertificateHtmlContent(html);
          }
        }
      } catch (e) {
        console.error("❌ Erreur chargement template:", e);
      }
    };
    loadLastTemplate();
  }, [courseId]);

  const handleImageUpload = (info, setState) => {
    const file = info.file?.originFileObj || info.file;
    const reader = new FileReader();
    reader.onload = () => {
      setState(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleInsertSignature = () => {
    if (!signaturePreview) {
      message.warning("Upload d'abord une image de signature (bouton 'Signature').");
      return;
    }
    const imgTag = buildSignatureImgTag(signaturePreview, signatureWidth, signatureHeight);

    if (editorRef.current) {
      let current = editorRef.current.getContent();
      current = signatureImgToPlaceholder(current).replace(/\{\{signature\}\}/gi, "");
      editorRef.current.setContent(current);
      editorRef.current.insertContent(imgTag);
      setCertificateHtmlContent(editorRef.current.getContent());
      message.success("✅ Signature insérée. Tu peux la déplacer dans l'éditeur.");
    } else {
      setCertificateHtmlContent((prev) =>
        signatureImgToPlaceholder(prev || "").replace(/\{\{signature\}\}/gi, imgTag)
      );
    }
  };

  const handleInsertCertNumber = () => {
    if (editorRef.current) {
      let current = editorRef.current.getContent();
      current = current.replace(
        /<p[^>]*>\s*Certificat\s*n[°ºo][^<]*\{\{certNumber\}\}[^<]*<\/p>/gi,
        ""
      );
      editorRef.current.setContent(current);
      editorRef.current.insertContent(CERT_NUMBER_LINE);
      setCertificateHtmlContent(editorRef.current.getContent());
      message.success("✅ Ligne numéro insérée. Déplace-la où tu veux.");
    } else {
      setCertificateHtmlContent((prev) => (prev || "") + "\n" + CERT_NUMBER_LINE);
    }
  };

  // 🔧 Bouton manuel : forcer le nettoyage des traits parasites
  const handleCleanLines = () => {
    let current = editorRef.current
      ? editorRef.current.getContent()
      : certificateHtmlContent;
    const cleaned = removeOldSignatureLines(current || "");
    if (editorRef.current) editorRef.current.setContent(cleaned);
    setCertificateHtmlContent(cleaned);
    message.success("✅ Lignes/traits parasites supprimés.");
  };

  const buildPreviewHtml = () => {
    let html = certificateHtmlContent || "";
    html = html
      .replace(/\{\{certNumber\}\}/gi, EXAMPLE_CERT_NUMBER)
      .replace(/\{\{date\}\}/gi, new Date().toLocaleDateString())
      .replace(
        /\{\{signature\}\}/gi,
        '<span style="color:#bbb;font-style:italic;">[signature non insérée]</span>'
      );
    return html;
  };

  const handleDownloadSaved = async () => {
    if (!courseId) {
      message.warning("Aucun cours sélectionné.");
      return;
    }

    setLoadingDownload(true);
    try {
      const template = await courseService.getLastTemplate(courseId);

      if (!template || !template.templateHtml) {
        message.warning("Aucun template sauvegardé. Sauvegardez d'abord un certificat.");
        setLoadingDownload(false);
        return;
      }

      let settings = {};
      try {
        settings = template.templateSettings ? JSON.parse(template.templateSettings) : {};
      } catch (e) {
        settings = {};
      }

      let html = removeOldSignatureLines(template.templateHtml);
      if (!/\{\{certNumber\}\}/i.test(html)) html += "\n" + CERT_NUMBER_LINE;
      html = placeholderToSignatureImg(
        html,
        settings.signaturePreview || signaturePreview,
        settings.signatureWidth || signatureWidth,
        settings.signatureHeight || signatureHeight
      );
      setCertificateHtmlContent(html);

      if (settings.backgroundColor) setBackgroundColor(settings.backgroundColor);
      if (settings.textColor) setTextColor(settings.textColor);
      if (settings.paddingTop) setPaddingTop(settings.paddingTop);
      if (settings.paddingBottom) setPaddingBottom(settings.paddingBottom);
      if (settings.useImageBackground !== undefined) setUseImageBackground(settings.useImageBackground);

      setTimeout(async () => {
        try {
          const canvas = await html2canvas(certRef.current, { scale: 2, useCORS: true });
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("landscape", "pt", "a4");
          pdf.addImage(imgData, "PNG", 0, 0, 842, 595);
          const safeCourse = (courseTitle || "certificat").replace(/\s+/g, "_");
          pdf.save(`${safeCourse}_template.pdf`);
          message.success("✅ Template téléchargé !");
        } catch (err) {
          console.error("Erreur PDF:", err);
          message.error("Erreur lors de la génération du PDF.");
        } finally {
          setLoadingDownload(false);
        }
      }, 500);
    } catch (error) {
      console.error("Erreur:", error);
      message.error("Impossible de récupérer le template sauvegardé.");
      setLoadingDownload(false);
    }
  };

  const generatePDF = async () => {
    try {
      const canvas = await html2canvas(certRef.current, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "pt", "a4");
      pdf.addImage(imgData, "PNG", 0, 0, 842, 595);
      const safeStudent = (studentName || "").replace(/\s+/g, "_");
      pdf.save(`${safeStudent}_certificat.pdf`);
    } catch (error) {
      console.error("Erreur PDF:", error);
      message.error("Erreur lors du téléchargement.");
    }
  };

  const generateImageBlob = async () => {
    try {
      const canvas = await html2canvas(certRef.current, { scale: 2, useCORS: true });
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png");
      });
    } catch (error) {
      console.error("Erreur image:", error);
      message.error("Impossible de générer l'image.");
    }
  };

  const handleSaveCertificate = async () => {
    if (!courseId) {
      message.warning("Aucun cours sélectionné.");
      return;
    }

    try {
      setLoadingSave(true);
      const blob = await generateImageBlob();
      if (!blob) {
        message.error("Erreur lors de la génération.");
        setLoadingSave(false);
        return;
      }

      const file = new File([blob], `${studentName}_certificat.png`, { type: "image/png" });
      const user = JSON.parse(localStorage.getItem("user"));
      const formateur = user?.username || "Formateur";

      const settings = {
        backgroundColor, textColor, logoWidth, logoHeight,
        useImageBackground, paddingTop, paddingBottom, threshold,
        backgroundWidth, backgroundHeight,
        backgroundPreview,
        logoPreview,
        signaturePreview,
        signatureWidth,
        signatureHeight,
        autoSignature: true,
        autoCertNumber: true,
      };

      // 1) Nettoyage maximal + signature -> placeholder
      // 1) DIAGNOSTIC : on désactive le nettoyage pour voir ce qui se passe
      console.log("🔍 [1] Contenu de l'éditeur:", certificateHtmlContent);
      let htmlToSave = certificateHtmlContent || "";
      console.log("🔍 [2] HTML avant signature placeholder:", htmlToSave);
      htmlToSave = signatureImgToPlaceholder(htmlToSave);
      console.log("🔍 [3] HTML après signature placeholder (sera envoyé):", htmlToSave);
      
      // 2) noms/dates -> placeholders
      if (courseTitle) {
        const escapedTitle = courseTitle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        htmlToSave = htmlToSave.replace(new RegExp(escapedTitle, 'gi'), '{{courseName}}');
      }
      if (studentName && studentName !== "Nom") {
        const escapedName = studentName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        htmlToSave = htmlToSave.replace(new RegExp(escapedName, 'gi'), '{{username}}');
      }
      const today = new Date().toLocaleDateString();
      htmlToSave = htmlToSave.replace(new RegExp(today.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), '{{date}}');

      // 3) code d'exemple -> placeholder
      htmlToSave = htmlToSave.replace(
        new RegExp(EXAMPLE_CERT_NUMBER.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        '{{certNumber}}'
      );

      // 4) garantir placeholders
      if (!/\{\{signature\}\}/i.test(htmlToSave)) {
        htmlToSave = htmlToSave.replace(
          /(Direction des programmes SAP[^<]*<\/p>)/i,
          '$1\n<p style="text-align:center;">{{signature}}</p>'
        );
        if (!/\{\{signature\}\}/i.test(htmlToSave)) {
          htmlToSave += '\n<p style="text-align:center;">{{signature}}</p>';
        }
      }
      if (!/\{\{certNumber\}\}/i.test(htmlToSave)) {
        htmlToSave += "\n" + CERT_NUMBER_LINE;
      }

      await courseService.uploadImageCertificatWithGlobal(
        file, courseId, formateur, htmlToSave, settings, globalTemplate
      );

      try {
        const reloaded = await courseService.getLastTemplate(courseId);
        if (reloaded) {
          setLastSavedInfo({
            date: reloaded.dateCreation,
            global: reloaded.globalTemplate,
          });
          if (reloaded.threshold) setThreshold(reloaded.threshold);
        }
      } catch (e) {
        console.warn("Rechargement template échoué:", e);
      }

      message.success(
        globalTemplate
          ? "✅ Template sauvegardé pour TOUS les cours !"
          : "✅ Template sauvegardé pour ce cours !"
      );
    } catch (error) {
      console.error("Erreur:", error);
      message.error("Impossible de sauvegarder.");
    } finally {
      setLoadingSave(false);
    }
  };

  const handleTestVerification = async () => {
    if (!courseId) {
      message.warning("Aucun cours sélectionné.");
      return;
    }
    try {
      setLoadingTest(true);
      const user = JSON.parse(localStorage.getItem("user"));
      const token = user?.accessToken;
      const userId = user?.id;

      const response = await axios.get(
        `${API_BASE}/certificats/generate?courseId=${courseId}&userId=${userId}`,
        { headers: { Authorization: "Bearer " + token } }
      );

      const data = response.data;
      const code = data.verificationCode;

      if (code) {
        message.success("✅ Certificat généré ! Ouverture de la page de vérification...");
        window.open(`/certificate/verify/${code}`, "_blank");
      } else if (data.alreadySent) {
        if (data.verificationCode) {
          window.open(`/certificate/verify/${data.verificationCode}`, "_blank");
        } else {
          message.info("Certificat déjà envoyé mais sans code de vérification. Réinitialisez l'envoi en BDD.");
        }
      } else {
        message.warning("Certificat généré mais aucun code de vérification retourné.");
        console.log("Réponse complète:", data);
      }
    } catch (error) {
      console.error("Erreur test:", error);
      const errMsg = error.response?.data?.message || error.message;
      message.error("Erreur : " + errMsg);
    } finally {
      setLoadingTest(false);
    }
  };

  return (
    <div style={{ padding: '12px 16px' }}>
      <Card
        title="Paramètres du certificat"
        size="small"
        style={{ marginBottom: 12 }}
        headStyle={{ fontSize: '12px', padding: '4px 12px', minHeight: '32px' }}
        bodyStyle={{ padding: '10px 12px' }}
      >
        <Space wrap size={[6, 6]} style={{ width: '100%' }}>
          <Upload showUploadList={false} customRequest={({ file }) => handleImageUpload({ file }, setLogoPreview)}>
            <Button icon={<UploadOutlined />} size="small" style={{ fontSize: '11px' }}>Logo</Button>
          </Upload>
          <Input type="number" min={30} value={logoWidth} onChange={(e) => setLogoWidth(Number(e.target.value))} suffix="px" style={{ width: 70, fontSize: '11px' }} size="small" />
          <Input type="number" min={30} value={logoHeight} onChange={(e) => setLogoHeight(Number(e.target.value))} suffix="px" style={{ width: 70, fontSize: '11px' }} size="small" />

          <Upload showUploadList={false} customRequest={({ file }) => handleImageUpload({ file }, setBackgroundPreview)}>
            <Button icon={<UploadOutlined />} size="small" style={{ fontSize: '11px' }}>Fond</Button>
          </Upload>
          <Checkbox checked={useImageBackground} onChange={(e) => setUseImageBackground(e.target.checked)} style={{ fontSize: '11px' }}>Utiliser</Checkbox>
          <Input type="number" min={100} value={backgroundWidth} onChange={(e) => setBackgroundWidth(Number(e.target.value))} suffix="px" style={{ width: 80, fontSize: '11px' }} size="small" />
          <Input type="number" min={100} value={backgroundHeight} onChange={(e) => setBackgroundHeight(Number(e.target.value))} suffix="px" style={{ width: 80, fontSize: '11px' }} size="small" />

          <Upload showUploadList={false} customRequest={({ file }) => handleImageUpload({ file }, setSignaturePreview)}>
            <Button icon={<UploadOutlined />} size="small" style={{ fontSize: '11px', borderColor: '#E8860C', color: '#E8860C', fontWeight: 600 }}>Signature</Button>
          </Upload>
          <Button
            icon={<HighlightOutlined />}
            size="small"
            onClick={handleInsertSignature}
            disabled={!signaturePreview}
            style={{ fontSize: '11px', background: signaturePreview ? '#E8860C' : undefined, borderColor: '#E8860C', color: signaturePreview ? '#fff' : '#E8860C' }}
          >
            Insérer signature
          </Button>
          <Input type="number" min={40} value={signatureWidth} onChange={(e) => setSignatureWidth(Number(e.target.value))} suffix="px" title="Largeur signature" style={{ width: 80, fontSize: '11px' }} size="small" />
          <Input type="number" min={20} value={signatureHeight} onChange={(e) => setSignatureHeight(Number(e.target.value))} suffix="px" title="Hauteur signature" style={{ width: 80, fontSize: '11px' }} size="small" />
          {signaturePreview && (
            <Button size="small" danger style={{ fontSize: '11px' }} onClick={() => setSignaturePreview(null)}>
              Retirer signature
            </Button>
          )}

          <Button
            size="small"
            onClick={handleInsertCertNumber}
            style={{ fontSize: '11px', borderColor: '#0054A6', color: '#0054A6' }}
          >
            Insérer n° certificat
          </Button>

          <Button
            size="small"
            onClick={handleCleanLines}
            style={{ fontSize: '11px', borderColor: '#cf1322', color: '#cf1322' }}
          >
            Nettoyer les traits
          </Button>

          <Input type="color" value={textColor} onChange={(e) => setTextColor(e.target.value)} title="Couleur texte" style={{ width: 32, height: 24, padding: 0 }} />
          <Input type="color" value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)} title="Couleur fond" style={{ width: 32, height: 24, padding: 0 }} />

          <Input type="number" value={logoTopSpacing} onChange={(e) => setLogoTopSpacing(Number(e.target.value))} suffix="px" title="Haut logo" style={{ width: 75, fontSize: '11px' }} size="small" />
          <Input type="number" value={logoMarginBottom} onChange={(e) => setLogoMarginBottom(Number(e.target.value))} suffix="px" title="Esp. logo" style={{ width: 75, fontSize: '11px' }} size="small" />
          <Input type="number" value={paddingTop} onChange={(e) => setPaddingTop(Number(e.target.value))} suffix="px" title="Padding haut" style={{ width: 75, fontSize: '11px' }} size="small" />
          <Input type="number" value={paddingBottom} onChange={(e) => setPaddingBottom(Number(e.target.value))} suffix="px" title="Padding bas" style={{ width: 75, fontSize: '11px' }} size="small" />
        </Space>
        <div style={{ marginTop: 6, fontSize: '11px' }}>
          <Typography.Text code style={{ fontSize: '10px' }}>QR Code Link :</Typography.Text>{' '}
          <Typography.Text copyable style={{ fontSize: '11px' }}>{qrLink}</Typography.Text>
        </div>
        <div style={{ marginTop: 4, fontSize: '11px' }}>
          <Typography.Text type="secondary" style={{ fontSize: '11px' }}>
            💡 Traits parasites supprimés automatiquement. Bouton <b>Nettoyer les traits</b> si besoin. Pour souligner un texte : sélectionne-le + bouton <b>U</b> dans l'éditeur.
          </Typography.Text>
        </div>
      </Card>

      <Row gutter={12}>
        <Col span={12}>
          <Editor
            apiKey="v5a0uyztwrsskf09mpu56qttcqnamjr2l64oxdagfxt9zvle"
            onInit={(evt, editor) => { editorRef.current = editor; }}
            value={certificateHtmlContent}
            init={{
              height: 480,
              menubar: true,
              plugins: ["link", "lists", "table", "paste", "code", "help"],
              toolbar: "undo redo | bold italic underline | forecolor backcolor | alignleft aligncenter alignright | code",
              content_style: "body { font-size: 13px; }",
              extended_valid_elements: "img[src|alt|style|data-signature|width|height|class]",
            }}
            onEditorChange={(content) => setCertificateHtmlContent(content)}
          />
        </Col>
        <Col span={12}>
          <div
            ref={certRef}
            style={{
              width: `${backgroundWidth}px`,
              height: `${backgroundHeight}px`,
              paddingTop: `${paddingTop}px`,
              paddingBottom: `${paddingBottom}px`,
              paddingLeft: "40px",
              paddingRight: "40px",
              background: useImageBackground
                ? `url(${backgroundPreview}) center/cover no-repeat`
                : backgroundColor,
              color: textColor,
              borderRadius: "16px",
              position: "relative",
              boxShadow: "0 0 10px rgba(0,0,0,0.2)",
              transform: "scale(0.85)",
              transformOrigin: "top left",
              overflow: "hidden",
            }}
          >
            {/* {logoPreview && (
              <img src={logoPreview} alt="Logo"
                style={{ position:"absolute", top:`${logoTopSpacing}px`, left:"30px",
                  width:`${logoWidth}px`, height:`${logoHeight}px`,
                  borderRadius:0, objectFit:"contain" }} />
            )} */}
            <div style={{ marginTop: `${logoTopSpacing + logoHeight + logoMarginBottom}px` }}>
              <div dangerouslySetInnerHTML={{ __html: buildPreviewHtml() }} />
            </div>

            <div style={{ position: "absolute", bottom: "28px", right: "30px" }}>
              <QRCodeCanvas value={qrLink} size={80} bgColor="white" fgColor="#000" />
            </div>
          </div>
        </Col>
      </Row>

      <div style={{
        marginTop: 12,
        padding: '10px 14px',
        background: '#fafafa',
        borderRadius: '8px',
        border: '1px solid #f0f0f0'
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: 'wrap', marginBottom: 10 }}>
          <Typography.Text style={{ fontSize: '12px', fontWeight: 500, color: "#555", background: "#f0f2f5", padding: "4px 10px", borderRadius: "6px" }}>
            🎯 Seuil (%)
          </Typography.Text>
          <Input
            type="number" min={0} max={100} value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            suffix="%" size="small"
            style={{ width: 80, borderRadius: "6px", fontSize: '12px' }}
          />

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tooltip title={globalTemplate
              ? "Ce template sera utilisé pour TOUS les cours qui n'ont pas de template spécifique"
              : "Ce template s'applique uniquement à ce cours"
            }>
              <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: 12 }} />
            </Tooltip>
            <Typography.Text style={{ fontSize: '12px', color: '#555' }}>
              {globalTemplate ? '🌐 Tous les cours' : '📌 Ce cours uniquement'}
            </Typography.Text>
            <Switch
              checked={globalTemplate}
              onChange={setGlobalTemplate}
              checkedChildren={<GlobalOutlined />}
              unCheckedChildren={<LinkOutlined />}
              size="small"
            />
          </div>
        </div>

        {lastSavedInfo && (
          <Alert
            message={`Dernier template : ${new Date(lastSavedInfo.date).toLocaleString()} ${lastSavedInfo.global ? '(Global)' : '(Ce cours)'}`}
            type="info"
            showIcon
            style={{ marginBottom: 10, fontSize: '11px', padding: '4px 10px' }}
            banner
          />
        )}

        <div style={{ display: "flex", gap: "8px", flexWrap: 'wrap' }}>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadSaved}
            loading={loadingDownload}
            size="small"
            style={{ fontSize: '12px', background: '#1890ff', borderColor: '#1890ff', color: '#fff', fontWeight: 600 }}
          >
            Télécharger dernier template
          </Button>

          <Button
            type="default"
            icon={<FilePdfOutlined />}
            onClick={generatePDF}
            size="small"
            style={{ fontSize: '12px' }}
          >
            Télécharger aperçu actuel
          </Button>

          <Button
            icon={<EyeOutlined />}
            loading={loadingTest}
            onClick={handleTestVerification}
            size="small"
            style={{ fontSize: '12px', borderColor: '#667eea', color: '#667eea' }}
          >
            Tester la vérification
          </Button>

          <Button
            icon={<SaveOutlined />}
            onClick={handleSaveCertificate}
            loading={loadingSave}
            size="small"
            style={{
              marginLeft: 'auto',
              backgroundColor: "#52c41a",
              borderColor: "#52c41a",
              color: "#fff",
              fontWeight: "bold",
              fontSize: '12px',
            }}
          >
            {globalTemplate ? 'Sauvegarder (Global)' : 'Sauvegarder (Ce cours)'}
          </Button>
        </div>
      </div>

      {uploadedImageLink && (
        <div style={{ marginTop: 8, fontSize: '11px' }}>
          <Typography.Text copyable style={{ fontSize: '11px' }}>
            Lien : <a href={uploadedImageLink} target="_blank" rel="noopener noreferrer">{uploadedImageLink}</a>
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

export default CertificateGenerator;